import {
  CalculateServiceCostResult,
  CheckFileResult,
  GetFileKindResult,
  IsCloseFormResult,
  SendFileResult,
  SendFilesResult,
  Service,
  UploadFileResult,
  UploadFileType,
  UploadFilesResult,
} from '@anispi-reporting-system/types';
import { chunk } from 'lodash';
import { API_URL } from './settings';

interface UploadFileProps {
  companyNo: string;
  officeNo: string;
  service: Service;
}

const uploadFile = async (props: UploadFileProps) => {
  const url = `${API_URL}/uploadFile`;

  const response = await fetch(url, {
    body: JSON.stringify(props),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    mode: 'cors',
  });

  const result = (await response.json()) as UploadFileResult;

  if (!response.ok) {
    result.success = false;
  }

  return result;
};

interface CheckFileProps {
  companyNo: string;
  officeNo: string;
  fileName: string;
  service: Service;
  uploadFileType: UploadFileType;
}

const checkFile = async (props: CheckFileProps) => {
  const url = `${API_URL}/checkFile`;

  const response = await fetch(url, {
    body: JSON.stringify(props),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    mode: 'cors',
  });

  const result = (await response.json()) as CheckFileResult;

  if (!response.ok) {
    result.success = false;
  }

  return result;
};

type CheckFilesProps = {
  companyNo: string;
  officeNo: string;
  fileName: string;
  service: Service;
};

const checkFiles = async (props: CheckFilesProps) => {
  const url = `${API_URL}/checkFiles`;

  const response = await fetch(url, {
    body: JSON.stringify(props),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    mode: 'cors',
  });

  const result = (await response.json()) as CheckFileResult;

  if (!response.ok) {
    result.success = false;
  }

  return result;
};

interface CalculateServiceCostProps {
  companyNo: string;
  officeNo: string;
  fileNameSe: string;
  fileNameSv: string;
  service: Service;
}

const calculateServiceCost = async (props: CalculateServiceCostProps) => {
  const url = `${API_URL}/calculateServiceCost`;

  const response = await fetch(url, {
    body: JSON.stringify(props),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    mode: 'cors',
  });

  const result = (await response.json()) as CalculateServiceCostResult;

  if (!response.ok) {
    result.success = false;
  }

  return result;
};

interface SendFileProps {
  companyName: string;
  companyNo: string;
  officeNo: string;
  fileNameSe: string;
  fileNameSv: string;
  service: Service;
}

const sendFile = async (props: SendFileProps) => {
  const url = `${API_URL}/sendFile`;

  const response = await fetch(url, {
    body: JSON.stringify(props),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    mode: 'cors',
  });

  const result = (await response.json()) as SendFileResult;

  if (!response.ok) {
    result.success = false;
  }

  return result;
};

const uploadFiles = async (props: { fileNum: number }) => {
  const url = `${API_URL}/uploadFiles`;

  const response = await fetch(url, {
    body: JSON.stringify(props),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    mode: 'cors',
  });

  const result = (await response.json()) as UploadFilesResult;

  if (!response.ok) {
    result.success = false;
  }

  return result;
};

const getFileKindResult = async (props: { fileNames: string[] }) => {
  const url = `${API_URL}/getFileKind`;

  const response = await fetch(url, {
    body: JSON.stringify(props),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    mode: 'cors',
  });

  const result = (await response.json()) as GetFileKindResult;

  if (!response.ok) {
    result.success = false;
  }

  return result;
};

const isCloseForm = async () => {
  const url = `${API_URL}/isCloseForm`;

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    mode: 'cors',
  });

  const result = (await response.json()) as IsCloseFormResult;

  if (!response.ok) {
    return true;
  }

  return result.isClose;
};

interface SendFilesProps {
  companyNo: string;
  officeNo: string;
  fileName: string;
  service: Service;
}

const sleep = (time: number) =>
  new Promise(resolve => {
    setTimeout(resolve, time);
  });

const chunkedSize = 25;

const sendFiles = async (props: SendFilesProps[], updateSentFileCount: (sentFileCount: number) => void) => {
  const url = `${API_URL}/sendFiles`;

  // 25件ずつ送信する
  const chunkedProps = chunk(props, chunkedSize);
  const result: SendFilesResult = {
    results: [],
    success: true,
  };

  // eslint-disable-next-line no-restricted-syntax
  for (const sendFilesProps of chunkedProps) {
    // eslint-disable-next-line no-await-in-loop
    const response = await fetch(url, {
      body: JSON.stringify({
        files: sendFilesProps,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
      mode: 'cors',
    });

    // eslint-disable-next-line no-await-in-loop
    const partialResult = (await response.json()) as SendFilesResult;

    result.results.push(...partialResult.results);

    if (!response.ok) {
      result.success = false;
    }

    if (props.length > chunkedSize) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(10000);
    }

    updateSentFileCount(sendFilesProps.length);
  }

  return result;
};

export { calculateServiceCost, checkFile, checkFiles, getFileKindResult, isCloseForm, sendFile, sendFiles, uploadFile, uploadFiles };
